import React from "react";
import {connect, DispatchProp} from "react-redux";
import {ListViewProps, ListViewState} from "../../shared/components/list-view/ListView";
import listViewStyles from "../../shared/components/list-view/list-view.module.css";
import {IQuestion} from "../../store/game/types";
import {IQuestionProgress} from "../../store/questions/types";
import {loadScene} from "../../store/routing/actions";
import {memoize} from "../../shared/utils/memoize";
import {IApplicationState} from "../../store";
import {showDialog} from "../../store/dialog-manager/actions";
import {IQuestionDialogOptions} from "../../store/dialog-manager/types";
import {cssHelper} from "../../shared/utils/cssHelper";
import groupBy from "../../shared/utils/groupBy";
import {trackError} from "../../store/log/actions";
import {buildRequest, buildUrl, getCurrentApiEndpoint} from "../../shared/utils/apiCall";
import {IApiSettings} from "../../store/settings/types";
import {HttpError} from "../../shared/utils/HttpError";
import {IQuestionApiResponse} from "../../store/game/sagas/types";
import {ListViewScene} from "../../shared/components/list-view/ListViewScene";
import playSound from "../../shared/sounds/playSound";
import i18n from "../../shared/i18n";

const SCROLL_OFFSET = 600;
const PAGE_SIZE = 100;

interface HistoryItem {
    date: string;
    question: IQuestion;
}

interface HistorySceneProps extends DispatchProp, ListViewProps {
    api: IApiSettings;
    signature?: string;
    locale: string;
    progress: Record<number, IQuestionProgress>;
}

class HistoryScene extends ListViewScene<HistoryItem, HistorySceneProps, ListViewState<HistoryItem>> {
    constructor(props: HistorySceneProps, context: any) {
        super(props, context);

        this.loadPageInternal = memoize(this.loadPageInternal.bind(this));
    }

    renderEntries() {
        if (!this.state.entries || !this.state.entries.length)
            return;

        const byDay = groupBy(this.state.entries, "date");

        const groups = Object.keys(byDay).map(date => (
            (
                <div className={listViewStyles.entry_group} key={date}>
                    <div className={listViewStyles.entry_group_caption}>{date}</div>
                    <div className={listViewStyles.entries}>
                        {byDay[date].map(entry => this.renderEntry(entry))}
                    </div>
                </div>
            )
        ));

        return (
            <>
                {groups}
            </>
        );
    }

    renderEntry(entry: HistoryItem) {
        const question = entry.question;

        if (!question)
            return;

        const isRight = this.props.progress[question.id].isRight
            ? i18n("answershistory.correctanswer")
            : i18n("answershistory.incorrectanswer");

        return (
            <div key={question.id} className={cssHelper(listViewStyles.entry, listViewStyles.entry_clickable).toString()} onClick={() => this.onQuestionClick(question.id)}>
                <div className={listViewStyles.picture} style={{ backgroundImage: `url('${question.thumbnailImageUrl}')`}}/>
                <div className={listViewStyles.entry_data}>
                    <div className={listViewStyles.entry_name} style={{ fontSize: 24 }}>
                        {question.title}
                    </div>
                    <div className={listViewStyles.entry_description} style={{ fontSize: 20 }}>
                        {isRight}
                    </div>
                </div>
            </div>
        );
    }

    getHeader() {
        return i18n("answershistory.header");
    }

    async loadPageInternal(page: number) {
        const {api, signature, locale} = this.props;

        const questionIds = Object.values(this.props.progress)
            .filter(question => !question.isSkipped)
            .orderByDescending(question => question.timestamp)
            .slice(page * this.props.pageSize, (page + 1) * this.props.pageSize)
            .reduce((questions, question) => ([...questions, question.questionId]), [] as number[]);

        if (!questionIds.length)
            return {
                entries: []
            };

        const apiEndpoint = getCurrentApiEndpoint(api.endpoints);
        const url = buildUrl(apiEndpoint, api.uriTemplate, {
            section: "questions",
            method: "get",
            questionIds: questionIds,
        });
        const request = buildRequest(signature!);
        const response = await fetch(url, request);
        if (!response.ok) {
            throw new HttpError("Request failed", {
                url,
                request
            });
        }

        const content: IQuestionApiResponse = await response.json();
        if (!content.success) {
            throw new HttpError("Request failed", {
                url,
                request
            });
        }

        return {
            entries: content.data.map(question => {
                const date = new Date(this.props.progress[question.id].timestamp);
                const isToday = date.toDateString() === new Date().toDateString();

                return {
                    question: question,
                    date: isToday
                        ? i18n("answershistory.today")
                        : date.toLocaleDateString
                            ? date.toLocaleDateString(locale, { month: "long", day: "numeric" })
                            : date.toDateString()
                }
            })
        };
    }

    protected hasAllDataLoaded(entries: HistoryItem[]) {
        return !entries;
    }

    back() {
        super.back();
        this.props.dispatch(loadScene.request("MainMenu"));
    }

    protected async trackError(message: string, error?: Error) {
        this.props.dispatch(trackError({
            category: "HistoryScene",
            message: message,
            error: error,
        }));
    }

    private onQuestionClick(questionId: number) {
        playSound("button-click");

        this.props.dispatch(showDialog({
            type: 'QuestionDialog',
            options: {
                questionId: questionId,
                continueButtonText: i18n("questionanswer.buttonsblock.backbutton")
            } as IQuestionDialogOptions
        }));
    }
}

export default connect(({ player, game, questions, settings }: IApplicationState) => ({
    api: settings.api,
    signature: player.signature,
    progress: questions.progress,
    locale: settings.general.locale,
    pageSize: PAGE_SIZE,
    scrollOffset: SCROLL_OFFSET,
}))(HistoryScene);